import type { SearchProvider } from '@agentteams/runner-request/ui/runtime';
import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';

export type SearchEntityType =
  | 'PLAN'
  | 'CO_ACTION'
  | 'COMPLETION_REPORT'
  | 'POST_MORTEM'
  | 'CONVENTION'
  | 'SKILL'
  | 'GITHUB_ISSUE'
  | 'GITHUB_PR'
  | 'GITLAB_ISSUE'
  | 'GITLAB_MERGE_REQUEST'
  | 'BITBUCKET_ISSUE'
  | 'BITBUCKET_PR'
  | 'LINEAR_ISSUE'
  | 'SENTRY_ISSUE'
  | 'COMMENT'
  | 'CODE_REVIEW'
  | 'DOCUMENT';

export type SearchPartialFailure = 'github' | 'gitlab' | 'bitbucket' | 'linear' | 'sentry';

export type SearchResultItem = {
  entityType: SearchEntityType;
  id: string;
  title: string;
  snippet: string | null;
  status: string | null;
  rank: number;
  contentTokenCount: number | null;
  createdAt: string;
  htmlUrl?: string | null;
  targetId?: string | null;
  targetEntityType?: SearchEntityType | null;
  // 보관(archive) 여부. Convention/Document만 true가 될 수 있다.
  archived?: boolean;
};

export type { SearchProvider } from '@agentteams/runner-request/ui/runtime';

export type SearchMeta = {
  total: number;
  truncatedByTokenBudget: boolean;
  availableProviders: SearchProvider[];
  semanticEnabled: boolean;
};

export type SearchResponse = {
  data: SearchResultItem[];
  partialFailures: SearchPartialFailure[];
  meta: SearchMeta;
};

export type SearchParams = {
  q?: string;
  types?: SearchEntityType[];
  limit?: number;
  maxTokens?: number;
};

export type SearchOptions = {
  signal?: AbortSignal;
};

export const searchEntities = async (
  token: string,
  projectId: string,
  params: SearchParams,
  options?: SearchOptions,
): Promise<SearchResponse> => {
  const queryParams = new URLSearchParams();
  if (params.q) queryParams.set('q', params.q);
  if (params.types && params.types.length > 0) {
    for (const t of params.types) {
      queryParams.append('types', t);
    }
  }
  if (params.limit) queryParams.set('limit', String(params.limit));
  if (params.maxTokens) queryParams.set('maxTokens', String(params.maxTokens));

  const url = `${getApiUrl()}/api/projects/${projectId}/search?${queryParams.toString()}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
    signal: options?.signal,
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to search: ${response.status}`);
  }

  const data = (await response.json()) as SearchResponse;
  return data;
};
